import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

export const DevOpsCulturePage = () => {
  return (
    <div className="min-h-screen bg-gray-950 dark:bg-gray-900 text-gray-300">
      <Helmet>
        <title>DevOps Culture Transformation | KOBRALABS</title>
        <meta name="description" content="Learn how to transform your organization's development and operations with proven DevOps practices and build a collaborative culture that accelerates delivery and improves software quality." />
        <meta name="keywords" content="DevOps culture, DevOps transformation, CI/CD, collaboration, software quality" />
      </Helmet>

      <main className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link to="/blog" className="text-blue-400 hover:text-blue-300 mb-8 inline-block">
              &larr; Back to all articles
            </Link>

            <article className="prose prose-invert lg:prose-xl max-w-none">
              <div className="mb-8">
                <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">DevOps Culture Transformation</h1>
                <div className="flex items-center text-gray-400 text-sm">
                  <span>Published on May 1, 2024</span>
                  <span className="mx-2">•</span>
                  <span>6 min read</span>
                  <span className="mx-2">•</span>
                  <span className="text-blue-400">DevOps</span>
                </div>
                <div className="flex items-center mt-4">
                  <img src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=100&q=80" alt="Alex Thompson" className="w-10 h-10 rounded-full mr-3" />
                  <span>By Alex Thompson</span>
                </div>
              </div>

              <p className="lead">
                DevOps is more than just a set of tools or processes; it's a cultural philosophy that emphasizes collaboration, communication, and integration between software development and IT operations teams. A successful DevOps transformation requires a fundamental shift in mindset and culture.
              </p>

              <img src="https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1200&q=80" alt="DevOps culture" className="rounded-lg my-8" />

              <h2>The Pillars of a Strong DevOps Culture</h2>
              <p>
                Building a thriving DevOps culture rests on several key pillars that foster a collaborative and high-performing environment.
              </p>
              <ul>
                <li><strong>Shared Ownership:</strong> In a DevOps culture, everyone is responsible for the quality and reliability of the product. Developers don't just write code; they own it from development through to production and beyond.</li>
                <li><strong>Continuous Learning:</strong> A commitment to continuous improvement is essential. Teams should regularly conduct blameless post-mortems to learn from failures and celebrate successes to reinforce best practices.</li>
                <li><strong>Automation First:</strong> Automating repetitive tasks is a cornerstone of DevOps. This reduces manual errors, frees up teams to focus on value-added work, and accelerates the delivery pipeline.</li>
                <li><strong>Customer-Centricity:</strong> DevOps teams are focused on delivering value to the customer. This involves gathering feedback early and often, and using it to iterate on the product.</li>
              </ul>

              <h2>Breaking Down Silos: The Heart of the Transformation</h2>
              <p>
                The traditional separation between development and operations teams often leads to friction, delays, and miscommunication. DevOps aims to break down these silos by creating cross-functional teams that work together towards a common goal.
              </p>
              <p>
                This can be achieved through:
              </p>
              <ul>
                <li><strong>Integrated Teams:</strong> Structuring teams to include members from development, operations, security, and quality assurance.</li>
                <li><strong>Shared Tools and Processes:</strong> Using a common toolchain for version control, CI/CD, and monitoring to create a single source of truth.</li>
                <li><strong>Open Communication:</strong> Fostering a culture of open and honest communication, where teams feel safe to share ideas, raise concerns, and admit mistakes.</li>
              </ul>

              <blockquote className="border-l-4 border-blue-500 pl-4 italic">
                "The goal of DevOps is to create a culture and an environment where building, testing, and releasing software can happen rapidly, frequently, and more reliably."
              </blockquote>

              <h2>The Role of Leadership in a DevOps Transformation</h2>
              <p>
                Leadership plays a critical role in driving a successful DevOps transformation. Leaders must champion the change, provide the necessary resources, and create a safe environment for experimentation and learning.
              </p>
              <p>
                Key leadership actions include:
              </p>
              <ul>
                <li><strong>Communicating the Vision:</strong> Clearly articulating the "why" behind the DevOps transformation and how it aligns with the organization's goals.</li>
                <li><strong>Empowering Teams:</strong> Giving teams the autonomy to make decisions and experiment with new ideas.</li>
                <li><strong>Investing in Training:</strong> Providing teams with the training and resources they need to develop new skills.</li>
                <li><strong>Measuring What Matters:</strong> Focusing on metrics that measure the flow of value to the customer, rather than traditional metrics like lines of code or number of deployments.</li>
              </ul>

              <h2>Getting Started with Your DevOps Transformation</h2>
              <p>
                A DevOps transformation is a journey, not a destination. It's best to start small, with a single team or project, and then scale what works to the rest of the organization.
              </p>
              <p>
                At KOBRALABS, we help organizations navigate this journey by providing expert guidance on tooling, processes, and cultural change. We believe that by fostering a culture of collaboration and continuous improvement, any organization can unlock the full potential of DevOps to deliver better software, faster.
              </p>
            </article>
          </motion.div>
        </div>
      </main>
    </div>
  );
}; 